/**
 * EDTS Panel
 *
 * Main panel for the External Data Transfer Specification document.
 * Displays sections grouped by category and a detected-sources table showing
 * which external vendor data streams were discovered in the protocol.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useState, useEffect } from 'react';
import SafeHtml from '../common/SafeHtml';
import { SectionStatusBadge, SectionFailureNotice } from '../common/SectionStatus';
import { sortSections, compareSectionNumbers } from '../../features/documents/sectionOrder';
import { regenerateHint } from '../../features/documents/sectionStatus';
import {
  excludedSourceLine,
  generationStatusNotice,
  sourceOriginBadge,
  sourceTypeLabel,
  transferModeLabel,
  type ExcludedSourceLike,
} from '../../features/documents/edtsDisplay';
import {
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc, dataManagementSubcollection } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import { studyDocumentDownloadOptions } from '../../features/downloads/studyDocumentDownloads';
import {
  Loader2,
  Play,
  RefreshCw,
  FileText,
  Database,
  ChevronDown,
  ChevronRight,
  BookOpen,
  AlertTriangle,
  ShieldCheck,
  FileCode,
  Lock,
  CheckCircle2,
  Archive,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/** The Origin column's badge colours, by sourceOriginBadge tone. */
const ORIGIN_BADGE_STYLES: Record<'pending' | 'user' | 'detected', string> = {
  pending: 'bg-amber-50 border-amber-200 text-amber-700',
  user: 'bg-violet-50 border-violet-200 text-violet-700',
  detected: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

/* ---------- Types ---------- */

interface DetectedSource {
  type: string;
  displayName: string;
  evidence: string;
  vendorMentioned?: string | null;
  /** Where the source came from: Study Settings or protocol detection. */
  origin?: 'user' | 'protocol';
  /** 'to_be_confirmed': found in the protocol, not yet confirmed in Study Settings. */
  confirmation?: 'confirmed' | 'to_be_confirmed';
  /** The system the data come from (set for the safety database). */
  system?: string | null;
  /** 'reconciliation_only': no transfer file; reconciled against the EDC only. */
  transferMode?: 'file_transfer' | 'reconciliation_only';
}

interface EDTSDoc {
  version: number;
  status: string;
  title: string;
  generatedAt: any;
  generatedBy: string;
  studyInfo?: any;
  detectedSources?: DetectedSource[];
  /** Source types the study does not use, each with a plain-language reason. */
  excludedSources?: ExcludedSourceLike[];
  /** 'incomplete' when any section could not be generated. */
  generationStatus?: 'complete' | 'incomplete';
  failedSectionCount?: number;
  sourceCount?: number;
  vendorCount?: number;
  _generating?: boolean;
  _generationStartedAt?: any;     // Firestore timestamp
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
}

interface EDTSSection {
  id: string;
  sectionId: string;
  number: number;
  title: string;
  group: string;
  content: string;
  status: string;
}

interface EDTSPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's edts card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

/* ---------- Constants ---------- */

const GROUP_COLORS: Record<string, { bg: string; border: string; text: string; icon: React.ReactNode }> = {
  introduction: { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   icon: <BookOpen className="h-4 w-4" /> },
  regulatory:   { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    icon: <ShieldCheck className="h-4 w-4" /> },
  sources:      { bg: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700',    icon: <Database className="h-4 w-4" /> },
  format:       { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    icon: <FileCode className="h-4 w-4" /> },
  security:     { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700',  icon: <Lock className="h-4 w-4" /> },
  validation:   { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: <CheckCircle2 className="h-4 w-4" /> },
  edge_cases:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   icon: <AlertTriangle className="h-4 w-4" /> },
  retention:    { bg: 'bg-stone-50',   border: 'border-stone-200',   text: 'text-stone-700',   icon: <Archive className="h-4 w-4" /> },
};

const STATUS_STYLES: Record<string, string> = {
  draft:       'bg-gray-100 text-gray-600',
  'in-review': 'bg-blue-100 text-blue-700',
  approved:    'bg-green-100 text-green-700',
  generated:   'bg-blue-100 text-blue-700',
};

const PROGRESS_STEPS = [
  'Loading protocol and study setup...',
  'Detecting external data sources from protocol facts...',
  'Generating regulatory framework section...',
  'Generating per-source variable metadata...',
  'Generating file format and transfer schedule sections...',
  'Generating transmission, security, and blinding sections...',
  'Generating validation and reconciliation sections...',
  'Generating special cases and retention sections...',
  'Building signature page...',
  'Finalizing document...',
];

/* ---------- Helpers ---------- */

function groupSections(sections: EDTSSection[]): Record<string, EDTSSection[]> {
  const groups: Record<string, EDTSSection[]> = {};
  for (const s of sections) {
    const key = (s.group || 'other').toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  }
  // Sort within each group
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => compareSectionNumbers(a.number, b.number));
  }
  return groups;
}

function getGroupStyle(group: string) {
  const key = group.toLowerCase();
  return GROUP_COLORS[key] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: <FileText className="h-4 w-4" /> };
}

function formatGroupLabel(group: string): string {
  return group
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function truncate(text: string, max: number): string {
  if (!text) return '';
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1) + '\u2026';
}

/* ---------- Component ---------- */

export default function EDTSPanel({ projectId, generating: hubGenerating = false }: EDTSPanelProps) {
  const [edtsDoc, setEdtsDoc] = useState<EDTSDoc | null>(null);
  const [sections, setSections] = useState<EDTSSection[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'sections' | 'sources'>('sections');
  const [loading, setLoading] = useState(true);
  // Shares its running state with the card's Download button (same group).
  const download = useDocumentDownload(projectId, 'edts');
  const { showToast } = useToast();

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS; // the lock window; shared with the backend

  // Detect in-flight generation from the Firestore marker (cross-session/cross-tab aware)
  const isGeneratingFromMarker = (() => {
    if (!edtsDoc?._generating) return false;
    const startedAt = edtsDoc._generationStartedAt;
    if (!startedAt?.toDate) return true; // No timestamp yet — assume fresh
    const startedMs = startedAt.toDate().getTime();
    const ageMs = Date.now() - startedMs;
    return ageMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!edtsDoc?._generating) return false;
    const startedAt = edtsDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    const startedMs = startedAt.toDate().getTime();
    return Date.now() - startedMs >= STALE_GENERATION_MS;
  })();

  // Combine local 'generating' state (in-tab fast path) with the Firestore marker
  // Folds in the Hub's in-flight state (the shared guard — see
  // EDTSPanelProps.generating) alongside the local fast path and the Firestore
  // marker, so a click on the card's Regenerate disables this one at once.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  /* --- Firestore: EDTS metadata --- */
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'edts'),
      (snapshot) => {
        if (snapshot.exists()) {
          setEdtsDoc(snapshot.data() as EDTSDoc);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading EDTS:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  /* --- Firestore: sections --- */
  useEffect(() => {
    if (!projectId || !edtsDoc) return;

    const sectionsRef = dataManagementSubcollection(projectId, 'edts', 'sections');

    const unsubscribe = onSnapshot(
      query(sectionsRef, orderBy('number')),
      (snapshot) => {
        const list: EDTSSection[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<EDTSSection, 'id'>),
        }));
        setSections(sortSections(list));
      },
      (err) => {
        console.error('Error loading EDTS sections:', err);
      }
    );

    return () => unsubscribe();
  }, [projectId, edtsDoc]);

  /* --- Generate EDTS --- */
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);

    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 15000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_EDTS_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('EDTS generation error:', err);
      showToast(err.message || 'Failed to generate EDTS', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  /* --- Derived data --- */
  const grouped = groupSections(sections);
  const detectedSources = edtsDoc?.detectedSources || [];
  const excludedSources = edtsDoc?.excludedSources || [];
  const incompleteNotice = edtsDoc ? generationStatusNotice(edtsDoc) : undefined;

  const statusLabel = edtsDoc?.status || 'draft';
  const statusStyle = STATUS_STYLES[statusLabel] || STATUS_STYLES.draft;

  /* --- Loading state --- */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  /* --- Not yet generated (or marker-only doc during first generation) --- */
  const hasRealContent = !!(edtsDoc && (edtsDoc.version || edtsDoc.title));
  if (!edtsDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate an External Data Transfer Specification covering how external vendor data (central labs, IRT,
            ePRO, imaging, safety databases, biomarkers, wearables, EHRs) flows into your clinical database,
            including file formats, transfer schedules, security, blinding, validation, reconciliation, and
            signature blocks for sponsor and vendor sign-off.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol, study setup, CRFs, and edit checks. The platform detects
            external data sources from your protocol and generates per-source subsections for variable metadata,
            file format, transfer schedule, blinding controls, and reconciliation procedures.
          </p>


          {edtsDoc?._generationError && !edtsDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(edtsDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {generating
                  ? 'Generating...'
                  : 'Generation in progress in the background...'}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 3-5 minutes as each section is generated individually.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate External Data Transfer Specification
            </button>
          )}
        </div>
      </div>
    );
  }

  /* --- EDTS exists: full UI --- */
  return (
    <div className="space-y-3">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">External Data Transfer Specification</h2>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
            {statusLabel === 'in-review' ? 'In Review' : statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {sections.length} section{sections.length !== 1 ? 's' : ''}
            {edtsDoc?.sourceCount != null && (
              <> &middot; {edtsDoc.sourceCount} source{edtsDoc.sourceCount !== 1 ? 's' : ''}</>
            )}
            {edtsDoc?.vendorCount != null && edtsDoc.vendorCount > 0 && (
              <> &middot; {edtsDoc.vendorCount} vendor{edtsDoc.vendorCount !== 1 ? 's' : ''}</>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Tab toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => setActiveTab('sections')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'sections'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="h-3.5 w-3.5" />
              Sections
            </button>
            <button
              onClick={() => setActiveTab('sources')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'sources'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Database className="h-3.5 w-3.5" />
              Detected Sources
              {detectedSources.length > 0 && (
                <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
                  {detectedSources.length}
                </span>
              )}
            </button>
          </div>

          {/* Download */}
          <DownloadButton
            download={download}
            options={studyDocumentDownloadOptions('edts', { projectId })}
            variant="secondary"
            size="sm"
          />
        </div>
      </div>

      {/* Error */}
      {/* Previous generation error */}
      {edtsDoc?._generationError && !edtsDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(edtsDoc._generationError)}
        </div>
      )}

      {/* Some sections could not be generated */}
      {incompleteNotice && !showGenerating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {incompleteNotice}
        </div>
      )}

      {/* Stale generation warning */}
      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
          You can try generating again.
        </div>
      )}

      {/* Generation progress overlay */}
      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-700 mb-1">
            {generating
              ? 'Generating...'
              : 'Generation in progress in the background...'}
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div>

      {/* Sections tab */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          {Object.entries(grouped).map(([group, groupSectionsList]) => {
            const style = getGroupStyle(group);
            return (
              <div key={group} className={`border ${style.border} rounded-lg overflow-hidden`}>
                {/* Group header */}
                <div className={`${style.bg} px-4 py-2.5 flex items-center gap-2`}>
                  <span className={style.text}>{style.icon}</span>
                  <h3 className={`text-sm font-semibold ${style.text}`}>
                    {formatGroupLabel(group)}
                  </h3>
                  <span className="text-xs text-gray-400 ml-auto">
                    {groupSectionsList.length} section{groupSectionsList.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Sections in group */}
                <div className="divide-y divide-gray-100 bg-white">
                  {groupSectionsList.map((section) => {
                    const isExpanded = expandedSectionId === section.id;
                    return (
                      <div key={section.id}>
                        {/* Section row */}
                        <button
                          onClick={() =>
                            setExpandedSectionId(isExpanded ? null : section.id)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="text-xs font-mono text-gray-400 w-8">
                            {section.number}
                          </span>
                          <span className="text-sm font-medium text-gray-800 flex-1">
                            {section.title}
                          </span>
                          <SectionStatusBadge section={section} />
                        </button>

                        <SectionFailureNotice section={section} regenerateHint={regenerateHint('Regenerate All Sections')} />

                        {/* Expanded content */}
                        {isExpanded && section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <SafeHtml
                              className="prose prose-sm max-w-none text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[60vh] overflow-y-auto"
                              html={section.content}
                            />
                          </div>
                        )}
                        {isExpanded && !section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <p className="text-sm text-gray-400 italic">No content generated yet.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {sections.length === 0 && !showGenerating && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No sections found. Generate the EDTS to create sections.
            </div>
          )}
        </div>
      )}

      {/* Detected Sources tab */}
      {activeTab === 'sources' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-gray-400" />
            <span className="text-xs text-gray-500">
              External data sources (detected from the protocol, or added in Study Settings)
            </span>
            <span className="text-xs text-gray-400 ml-auto">
              {detectedSources.length} source{detectedSources.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">
                      Source Type
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-48">
                      Display Name
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-24">
                      Origin
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-40">
                      Vendor
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-40">
                      System
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-40">
                      Transfer
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Evidence
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detectedSources.map((src, idx) => {
                    const isUserSpecified = src.origin === 'user' || src.evidence === 'User-specified';
                    const transfer = transferModeLabel(src.transferMode);
                    const badge = sourceOriginBadge(src);
                    return (
                      <tr key={`${src.type}-${idx}`} className="hover:bg-gray-50 transition-colors align-top">
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {sourceTypeLabel(src.type)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {src.displayName}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${ORIGIN_BADGE_STYLES[badge.tone]}`}
                            title={
                              badge.tone === 'pending'
                                ? 'Found in the protocol. Confirm it in Study Settings.'
                                : undefined
                            }
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {src.vendorMentioned ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700 text-[10px] font-medium">
                              {src.vendorMentioned}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Not specified</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {src.system || ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {transfer || ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 max-w-xl">
                          {isUserSpecified ? (
                            <span className="text-gray-400 italic">Declared in Study Settings</span>
                          ) : (
                            truncate(src.evidence || '', 220)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {detectedSources.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                        No sources detected. Add sources in Study Settings or regenerate after uploading a protocol.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Source types the study does not use */}
          {excludedSources.length > 0 && (
            <div className="border border-gray-200 rounded-lg bg-white px-4 py-3">
              <h4 className="text-xs font-semibold text-gray-700">
                Not used in this study ({excludedSources.length})
              </h4>
              <ul className="mt-1.5 ml-4 list-disc text-xs text-gray-600 space-y-0.5">
                {excludedSources.map((src, idx) => (
                  <li key={`${src.type}-${idx}`}>{excludedSourceLine(src)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      </div>{/* end scrollable content area */}

      {/* Regenerate option */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (confirm('Regenerate the entire EDTS with the latest study data? Existing content will be overwritten.')) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate All Sections
        </button>
      </div>
    </div>
  );
}
